import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import staticDataService, { StaticData } from '../services/staticDataService';
import ImageCarousel from './ImageCarousel';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Handle logout functionality
  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRole');
    
    // Close dropdown
    setShowProfileDropdown(false);
    
    // Navigate to login page
    window.location.href = '/';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-avatar')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const [showCarouselModal, setShowCarouselModal] = useState(false);
  const [sliderKey, setSliderKey] = useState(0);
  const [currentWaterSlide, setCurrentWaterSlide] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaveDates, setLeaveDates] = useState<number[]>([15, 22]); // Example leave dates
  
  // Awards data structure matching the reference design
  const [awardsData, setAwardsData] = useState([
    { id: 1, name: 'John Smith', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'PRIDEOF JWIL', position: { top: '55%', left: '8%' }, badgePosition: { top: '45%', left: '12%' } },
    { id: 2, name: 'Sarah Johnson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'DIVERSITY CHAMPION', position: { top: '35%', left: '22%' }, badgePosition: { top: '40%', left: '26%' } },
    { id: 3, name: 'Mike Wilson', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'STAR CONTRIBUTOR', position: { top: '15%', left: '45%' }, badgePosition: { top: '25%', left: '49%' } },
    { id: 4, name: 'Emily Davis', image: '/assets/images/manoj-kumar-mishra.jpg', award: '#CONTRIBUTOR', position: { top: '8%', left: '65%' }, badgePosition: { top: '18%', left: '69%' } },
    { id: 5, name: 'Robert Brown', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'THE HIGHFLYER AWARD', position: { top: '8%', left: '82%' }, badgePosition: { top: '18%', left: '86%' } },
    { id: 6, name: 'Lisa Anderson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'CEO GOLD CLUB', position: { top: '45%', left: '88%' }, badgePosition: { top: '55%', left: '92%' } },
    { id: 7, name: 'David Miller', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'THE SHEROES AWARD', position: { top: '75%', left: '25%' }, badgePosition: { top: '85%', left: '29%' } },
    { id: 8, name: 'Jennifer Wilson', image: '/assets/images/manoj-kumar-mishra.jpg', award: 'THE OPERA AWARD', position: { top: '75%', left: '45%' }, badgePosition: { top: '85%', left: '49%' } },
    { id: 9, name: 'James Taylor', image: '/assets/images/prasenjit-mukherjee.jpg', award: 'YOUNG ACHIEVER AWARD', position: { top: '75%', left: '65%' }, badgePosition: { top: '85%', left: '69%' } }
  ]);
  const [showCeoDropdown, setShowCeoDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('JWIL Latest Updates');
  const [userRole, setUserRole] = useState('Super Admin'); // This would come from auth context
  
  // Company Policies data structure
  const [companyPolicies] = useState([
    {
      id: 1,
      title: 'Employee Handbook',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/employee-handbook.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 15, 2024'
    },
    {
      id: 2,
      title: 'Code of Conduct',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/code-of-conduct.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 10, 2024'
    },
    {
      id: 3,
      title: 'Leave Policy',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/leave-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 08, 2024'
    },
    {
      id: 4,
      title: 'IT Security Guidelines',
      category: 'IT',
      type: 'link',
      link: 'https://jwil.com/security-guidelines',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Dec 05, 2024'
    },
    {
      id: 5,
      title: 'Travel & Expense Policy',
      category: 'Finance',
      type: 'document',
      documentUrl: '/assets/documents/travel-expense-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Dec 01, 2024'
    },
    {
      id: 6,
      title: 'Health & Safety Guidelines',
      category: 'General',
      type: 'link',
      link: 'https://jwil.com/health-safety',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Nov 28, 2024'
    },
    {
      id: 7,
      title: 'Performance Management',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/performance-management.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 25, 2024'
    },
    {
      id: 8,
      title: 'Remote Work Policy',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/remote-work-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 20, 2024'
    },
    {
      id: 9,
      title: 'Data Privacy Policy',
      category: 'IT',
      type: 'document',
      documentUrl: '/assets/documents/data-privacy-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 15, 2024'
    },
    {
      id: 10,
      title: 'Workplace Harassment Policy',
      category: 'HR',
      type: 'document',
      documentUrl: '/assets/documents/workplace-harassment-policy.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Nov 10, 2024'
    },
    {
      id: 11,
      title: 'Environmental Policy',
      category: 'General',
      type: 'link',
      link: 'https://jwil.com/environmental-policy',
      icon: '/assets/images/link-icon.svg',
      uploadDate: 'Nov 05, 2024'
    },
    {
      id: 12,
      title: 'Quality Management System',
      category: 'General',
      type: 'document',
      documentUrl: '/assets/documents/quality-management-system.pdf',
      icon: '/assets/images/doc-icon.svg',
      uploadDate: 'Oct 30, 2024'
    }
  ]);
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [showAllSnippets, setShowAllSnippets] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  
  // JWIL Updates data structure
  const [jwilUpdates] = useState([
    {
      title: 'Jal Jeevan Mission Contribution',
      description: 'JWIL is proud to contribute to ensuring every rural household has access to clean water',
      date: 'Dec 15, 2024'
    },
    {
      title: 'Diwali Celebration',
      description: 'JWIL celebrated Diwali with great enthusiasm and joy across all offices',
      date: 'Dec 12, 2024'
    },
    {
      title: 'Employee Portal Launch',
      description: 'New employee portal launched with enhanced features and user experience',
      date: 'Dec 10, 2024'
    },
    {
      title: 'New Project Announcement',
      description: 'JWIL announces new water treatment project in Maharashtra',
      date: 'Dec 08, 2024'
    },
    {
      title: 'Q3 Results Published',
      description: 'JWIL reports strong Q3 performance with 25% growth in revenue',
      date: 'Dec 05, 2024'
    },
    {
      title: 'Team Building Event',
      description: 'Annual team building event conducted successfully at Goa office',
      date: 'Dec 01, 2024'
    },
    {
      title: 'New Office Opening',
      description: 'JWIL opens new regional office in Bangalore to expand operations',
      date: 'Nov 28, 2024'
    },
    {
      title: 'Sustainability Initiative',
      description: 'JWIL launches new sustainability initiative to reduce carbon footprint',
      date: 'Nov 25, 2024'
    },
    {
      title: 'Training Program Launch',
      description: 'New employee training program launched for skill development',
      date: 'Nov 20, 2024'
    },
    {
      title: 'Award Recognition',
      description: 'JWIL receives Best Water Management Company award for 2024',
      date: 'Nov 15, 2024'
    }
  ]);
  
  // Snippets data structure
  const [snippets] = useState([
    {
      id: 1,
      title: "Company Annual Report 2024",
      description: "Comprehensive overview of our achievements and financial performance this year.",
      image: "/assets/images/update-img.png",
      type: "image",
      link: "/assets/images/banner-image.jpg",
      date: "Dec 15, 2024"
    },
    {
      id: 2,
      title: "CEO Leadership Training Video",
      description: "Exclusive training session on leadership principles and team management.",
      image: "/assets/images/manoj-kumar-mishra.jpg", 
      type: "video",
      link: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      date: "Dec 10, 2024"
    },
    {
      id: 3,
      title: "Employee Handbook 2024",
      description: "Updated policies, procedures, and guidelines for all employees.",
      image: "/assets/images/doc-icon.svg",
      type: "ebook",
      link: "https://example.com/employee-handbook-2024.pdf",
      date: "Dec 5, 2024"
    },
    {
      id: 4,
      title: "Team Building Workshop Photos",
      description: "Memorable moments from our recent team building activities and workshops.",
      image: "/assets/images/prasenjit-mukherjee.jpg",
      type: "image", 
      link: "/assets/images/banner-image.jpg",
      date: "Nov 28, 2024"
    },
    {
      id: 5,
      title: "Product Demo Presentation",
      description: "Latest product features and demonstrations for our new software release.",
      image: "/assets/images/rocket-lunch.gif",
      type: "video",
      link: "https://www.youtube.com/embed/ScMzIvxBSi4",
      date: "Nov 20, 2024"
    },
    {
      id: 6,
      title: "Financial Planning Guide",
      description: "Comprehensive guide for personal and professional financial planning strategies.",
      image: "/assets/images/preseverance.png",
      type: "ebook",
      link: "https://example.com/financial-planning-guide.pdf", 
      date: "Nov 15, 2024"
    },
    {
      id: 7,
      title: "Office Renovation Progress",
      description: "Before and after photos of our office renovation project completion.",
      image: "/assets/images/rishabh-sethi.jpg",
      type: "image",
      link: "/assets/images/banner-image.jpg",
      date: "Nov 10, 2024"
    },
    {
      id: 8,
      title: "Quarterly Review Meeting",
      description: "Recording of our Q4 quarterly review meeting and performance discussions.",
      image: "/assets/images/flag.gif",
      type: "video",
      link: "https://www.youtube.com/embed/jNQXAC9IVRw",
      date: "Nov 5, 2024"
    }
  ]);

  // Sample data for events
  const [events] = useState([
    {
      id: 1,
      title: "Annual Company Retreat 2024",
      description: "Join us for our annual company retreat featuring team building activities, strategic planning sessions, and networking opportunities.",
      entryFee: "Free for employees",
      eventDate: "Jan 15, 2025",
      eventTime: "9:00 AM - 6:00 PM",
      link: "https://example.com/company-retreat-2024",
      image: "/assets/images/banner-image.jpg"
    },
    {
      id: 2,
      title: "Tech Innovation Workshop",
      description: "Explore the latest technologies and innovation trends that will shape our industry in the coming years.",
      entryFee: "$50",
      eventDate: "Jan 22, 2025", 
      eventTime: "2:00 PM - 5:00 PM",
      link: "https://example.com/tech-workshop-2025",
      image: "/assets/images/rocket-lunch.gif"
    },
    {
      id: 3,
      title: "Leadership Development Seminar",
      description: "Enhance your leadership skills with expert speakers and interactive workshops designed for managers and team leads.",
      entryFee: null,
      eventDate: "Feb 5, 2025",
      eventTime: "10:00 AM - 4:00 PM", 
      link: "https://example.com/leadership-seminar-2025",
      image: "/assets/images/manoj-kumar-mishra.jpg"
    },
    {
      id: 4,
      title: "Quarterly All-Hands Meeting",
      description: "Company-wide meeting to discuss Q1 results, upcoming projects, and strategic initiatives for the next quarter.",
      entryFee: null,
      eventDate: "Feb 12, 2025",
      eventTime: "3:00 PM - 4:30 PM",
      link: "https://example.com/all-hands-q1-2025",
      image: "/assets/images/astronaut-flag.gif"
    },
    {
      id: 5,
      title: "Employee Wellness Fair",
      description: "Discover wellness resources, health screenings, fitness activities, and mental health support services.",
      entryFee: "Free",
      eventDate: "Feb 20, 2025",
      eventTime: "11:00 AM - 3:00 PM",
      link: "https://example.com/wellness-fair-2025",
      image: "/assets/images/preseverance.png"
    },
    {
      id: 6,
      title: "Product Launch Celebration",
      description: "Celebrate the launch of our newest product with demos, presentations, and networking with the development team.",
      entryFee: "Free for employees",
      eventDate: "Mar 1, 2025",
      eventTime: "6:00 PM - 9:00 PM",
      link: "https://example.com/product-launch-2025",
      image: "/assets/images/flag.gif"
    }
  ]);
  
  // Debug state changes
  useEffect(() => {
    console.log('showCeoDropdown changed to:', showCeoDropdown);
  }, [showCeoDropdown]);
  
  useEffect(() => {
    console.log('showSearchDropdown changed to:', showSearchDropdown);
  }, [showSearchDropdown]);

  // Force slider reinitialization on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setSliderKey(prev => prev + 1);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const [bannerData, setBannerData] = useState<StaticData[]>([]);
  const [ceoData, setCeoData] = useState<StaticData[]>([]);
  const [applicationData, setApplicationData] = useState<StaticData[]>([]);
  const [loading, setLoading] = useState(true);


  const handleSearchFocus = () => {
    console.log('Search focused, showing dropdown');
    setShowSearchDropdown(true);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    console.log('Search blurred, hiding dropdown');
    // Don't hide if clicking on dropdown items
    if (e.relatedTarget && (e.relatedTarget as HTMLElement).closest('.search-results-dropdown')) {
      return;
    }
    setTimeout(() => setShowSearchDropdown(false), 200);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Search changed:', e.target.value);
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
    const todayDate = today.getDate();

    const calendarGrid = [];
    
    // Create a complete 6-week grid (42 cells total)
    const totalCells = 42;
    let dayCounter = 1;
    
    for (let i = 0; i < totalCells; i++) {
      if (i < 7) {
        // First row: day names
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        calendarGrid.push(
          <div key={`dayname-${i}`} className="day-name">
            {dayNames[i]}
          </div>
        );
      } else {
        // Calculate position in the calendar grid (excluding header row)
        const gridPosition = i - 7;
        
        if (gridPosition < firstDay || dayCounter > daysInMonth) {
          // Empty cell
          calendarGrid.push(<div key={`empty-${i}`} className="empty-day"></div>);
        } else {
          // Day cell
          let className = 'day';
          
          if (isCurrentMonth && dayCounter === todayDate) {
            className += ' today';
          }
          
          if (leaveDates.includes(dayCounter)) {
            className += ' absent';
          }

          calendarGrid.push(
            <div key={`day-${dayCounter}`} className={className}>
              {dayCounter}
            </div>
          );
          dayCounter++;
        }
      }
    }

    return calendarGrid;
  };

  const handleApplicationClick = (link: string) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  };

  const handlePolicyClick = (policy: any) => {
    if (policy.type === 'document') {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = policy.documentUrl;
      link.download = policy.title + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (policy.type === 'link') {
      // Open link in new tab
      window.open(policy.link, '_blank');
    }
  };

  const handleSnippetClick = (snippet: any) => {
    if (snippet.type === 'image') {
      setSelectedMedia(snippet);
      setShowImageModal(true);
    } else if (snippet.type === 'video') {
      setSelectedMedia(snippet);
      setShowVideoModal(true);
    } else if (snippet.type === 'ebook') {
      window.open(snippet.link, '_blank');
    }
  };

  const handleEventClick = (event: any) => {
    window.open(event.link, '_blank');
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedMedia(null);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedMedia(null);
  };

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        setLoading(true);
        const [banners, ceo, applications] = await Promise.all([
          staticDataService.getStaticDataByType('banner'),
          staticDataService.getStaticDataByType('ceo'),
          staticDataService.getStaticDataByType('application')
        ]);
        setBannerData(banners);
        setCeoData(ceo);
        setApplicationData(applications);
      } catch (error) {
        console.error('Error fetching static data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaticData();
  }, []);

  return (
    <>
      {/* Header */}
      <header className="main-header" style={{overflow: 'visible'}}>
        <div className="container" style={{overflow: 'visible'}}>
          <div className="header-content" style={{overflow: 'visible'}}>
            {/* Logo */}
            <div className="logo-container">
              <a href="#" className="logo" onClick={handleLogout}>
                <img src="/assets/images/jwil-logo.svg" alt="JWIL Logo" className="logo-img" />
              </a>
            </div>

            {/* Search Bar */}
            <div className="search-container">
              <div className="search-box">
                <img src="/assets/images/call.svg" alt="Call Icon" className="search-icon-img" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Search input clicked');
                    setShowSearchDropdown(true);
                  }}
                />
                <button type="button" className="clear-search-btn" onClick={clearSearch}>×</button>
                <div className="search-results-dropdown" style={{
                  display: showSearchDropdown ? 'block' : 'none',
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <ul className="search-results-list">
                    <li className="search-result-item">
                      <div className="result-info">
                        <p className="result-name">Anoop Sharma</p>
                        <p className="result-title">Software Developer</p>
                        <p className="result-contact">anoop.sharma@jwil.com • +91 5454578678</p>
                      </div>
                      <div className="result-ext">O33</div>
                    </li>
                    <li className="search-result-item active">
                      <div className="result-info">
                        <p className="result-name">Prasenjit Mukherjee</p>
                        <p className="result-title">Head IT & Digital</p>
                        <p className="result-contact">prasenjit.mukherjee@jwil.com • +91 9876543210</p>
                      </div>
                      <div className="result-ext">O42</div>
                    </li>
                    <li className="search-result-item">
                      <div className="result-info">
                        <p className="result-name">Manoj Kumar Mishra</p>
                        <p className="result-title">Engineering Team</p>
                        <p className="result-contact">manoj.mishra@jwil.com • +91 8765432109</p>
                      </div>
                      <div className="result-ext">O15</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Ticker */}
            <div className="ticker-bar">
              <div className="ticker-content">
                <div className="ticker-text-wrapper">
                  <p className="ticker-text">{bannerData[0]?.content || "Bring harmony to team expenses with budget limits and real-time monitoring."}</p>
                </div>
                <span className="icon-edit"></span>
              </div>
            </div>

            {/* User Profile */}
            <div className="user-profile" style={{overflow: 'visible'}}>
              <div className="user-greeting">
                <span>Good Morning:</span>
                <p>Purnendu Jha</p>
              </div>
              <div className="user-avatar" onClick={() => {
                console.log('Avatar clicked, current state:', showProfileDropdown);
                setShowProfileDropdown(!showProfileDropdown);
              }}>
                <img src="/assets/images/profile-avatar.jpg" alt="User Avatar" className="avatar-img" />
                {showProfileDropdown && (
                  <div 
                    className="profile-dropdown-portal"
                    style={{
                      position: 'absolute',
                      top: '110%',
                      right: '-10px',
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px',
                      zIndex: 99999,
                      padding: '8px 0'
                    }}
                  >
                    <div className="dropdown-arrow" style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '20px',
                      width: '0',
                      height: '0',
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderBottom: '8px solid white'
                    }}></div>
                    {(userRole !== 'Employee') && (
                      <button className="dropdown-item" onClick={() => window.location.href = '/edit-content'} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#333',
                        cursor: 'pointer'
                      }}>
                        <i className="icon-edit"></i>
                        Edit
                      </button>
                    )}
                    {userRole === 'Super Admin' && (
                      <button className="dropdown-item" onClick={() => console.log('User Management')} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#333',
                        cursor: 'pointer'
                      }}>
                        <i className="icon-users"></i>
                        User Management
                      </button>
                    )}
                    <button className="dropdown-item logout" onClick={handleLogout} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#dc3545',
                      cursor: 'pointer',
                      borderTop: '1px solid #e9ecef'
                    }}>
                      <i className="icon-logout"></i>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>


      <main className="main-content">
        {/* Info Cards Section */}
        <section className="info-cards-section">
          <div className="container">
            <div className="info-cards-grid">
              {/* CEO Message Card */}
              <div 
                className="info-card ceo-message-card" 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('CEO card mouse down, current state:', showCeoDropdown);
                  setShowCeoDropdown(prev => {
                    console.log('Toggling from', prev, 'to', !prev);
                    return !prev;
                  });
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <div className="ceo-message-content">
                  <img src="/assets/images/md-ceo.svg" alt="MD & CEO" className="ceo-image" />
                  <div className="ceo-message-content">
                    <div className="ceo-message-text">
                      <p>Message from</p>
                      <h3>MD & CEO</h3>
                    </div>
                    <img 
                      src="/assets/images/down-arrow.svg" 
                      alt={showCeoDropdown ? "Up Arrow" : "Down Arrow"} 
                      className="down-arrow"
                      style={{
                        transform: showCeoDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                    <img src="/assets/images/wave.svg" alt="Wave" className="wave" />
                  </div>
                </div>
                <div className="ceo-message-dropdown" style={{ 
                  display: showCeoDropdown ? 'block' : 'none'
                }}>
                  <div className="ceo-testimonial-card">
                    <div className="testimonial-content">
                      <div className="testimonial-image">
                        <img src="/assets/images/md-ceo.svg" alt="Rishabh Sethi" className="ceo-photo" />
                      </div>
                      <div className="testimonial-text">
                        <div className="quote-text">
                          <p>{ceoData[0]?.content || "As we continue to grow and evolve, I want to take a moment to express my gratitude to each one of you. Your dedication, creativity, and teamwork are what fuel our success. Every day, I am inspired by your passion and commitment to excellence, and together, we are building a future that we can all be proud of."}</p>
                          <div className="quote-icon">
                            <img src="/assets/images/quote.svg" alt="Quote Icon" />
                          </div>
                        </div>
                        <div className="ceo-info">
                          <div className="ceo-name">{(ceoData[0] as any)?.ceo_name || 'RISHABH SETHI'}</div>
                          <div className="ceo-title">{(ceoData[0] as any)?.ceo_position || 'MD & CEO'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="vision-mission-cards">
                {/* Vision Card */}
                <div className="info-card vision-card">
                  <div className="card-header">
                    <div className="card-icon-wrapper vision-icon">
                      <span className="icon-rocket">
                        <img src="/assets/images/rocket-lunch.gif" alt="Rocket" className="vision-icon" />
                      </span>
                    </div>
                    <h4 className="card-title">VISION</h4>
                  </div>
                  <div className="card-body">
                    <p>To lead in water management by delivering quality, reliable solutions that improve lives.</p>
                  </div>
                </div>

                {/* Mission Card */}
                <div className="info-card mission-card">
                  <div className="card-header">
                    <div className="card-icon-wrapper mission-icon">
                      <span className="icon-astronaut">
                        <img src="/assets/images/astronaut-flag1.gif" alt="Rocket" className="mission-icon" />
                      </span>
                    </div>
                    <h4 className="card-title">MISSION</h4>
                  </div>
                  <div className="card-body">
                    <p>Growing fast by leveraging top technology, expert teams, and strong systems.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Essentials */}
        <section className="access-essentials-section">
          <div className="container">
            <div className="access-essentials">
              <div className="title">Application &<br />Quick Links</div>
              <div className="apps">
                {applicationData.map((app) => (
                  <div 
                    key={app.id} 
                    className="app-card"
                    onClick={() => handleApplicationClick(app.link || '')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="app-icon">
                      <img 
                        src={app.icon || "/assets/images/access-icon.png"} 
                        alt={app.title || "Application"} 
                      />
                    </div>
                    <span>{app.title}</span>
                    <img src="/assets/images/wave.svg" alt="Wave" className="wave" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Main Cards Section */}
        <section className="main-cards-section">
          <div className="container">
            <div className="main-cards-grid">
              {/* Water Treatment Carousel */}
              <div className="water-treatment-carousel">
                <Slider
                  dots={false}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  autoplay={true}
                  autoplaySpeed={4000}
                  arrows={false}
                  beforeChange={(current, next) => setCurrentWaterSlide(next)}
                >
                  <div>
                    <div className="card water-treatment-card" onClick={() => setShowCarouselModal(true)}>
                      <div className="card-content">
                        <h3>Water Treatment Solutions</h3>
                        <p>Sewage Treatment Plant | Effluent Treatment Plant | Tertiary Treatment Plants (TTPs) are all involved in wastewater treatment...</p>
                      </div>
                      <div className="card-dots">
                        <span className={currentWaterSlide === 0 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 1 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 2 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 3 ? "active" : ""}></span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="card water-treatment-card" onClick={() => setShowCarouselModal(true)}>
                      <div className="card-content">
                        <h3>Advanced Filtration Systems</h3>
                        <p>State-of-the-art membrane bioreactor technology for enhanced water purification and treatment efficiency...</p>
                      </div>
                      <div className="card-dots">
                        <span className={currentWaterSlide === 0 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 1 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 2 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 3 ? "active" : ""}></span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="card water-treatment-card" onClick={() => setShowCarouselModal(true)}>
                      <div className="card-content">
                        <h3>Industrial Water Solutions</h3>
                        <p>Comprehensive industrial water treatment solutions including reverse osmosis, ion exchange, and chemical treatment processes...</p>
                      </div>
                      <div className="card-dots">
                        <span className={currentWaterSlide === 0 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 1 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 2 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 3 ? "active" : ""}></span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="card water-treatment-card" onClick={() => setShowCarouselModal(true)}>
                      <div className="card-content">
                        <h3>Smart Water Management</h3>
                        <p>IoT-enabled smart water management systems for real-time monitoring and automated control processes...</p>
                      </div>
                      <div className="card-dots">
                        <span className={currentWaterSlide === 0 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 1 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 2 ? "active" : ""}></span>
                        <span className={currentWaterSlide === 3 ? "active" : ""}></span>
                      </div>
                    </div>
                  </div>
                </Slider>
              </div>

              {/* Employee Card Container */}
              <div className="employee-card">
                {/* New Joinee Carousel */}
                <div className="new-joinee-carousel">
                  <Slider
                    key={`new-joinee-slider-${sliderKey}`}
                    dots={true}
                    infinite={true}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    autoplay={true}
                    autoplaySpeed={4500}
                    arrows={false}
                    adaptiveHeight={false}
                    lazyLoad="ondemand"
                    customPaging={(i) => (
                      <div className="custom-dot">
                        <span></span>
                      </div>
                    )}
                    dotsClass="slick-dots custom-dots"
                  >
                    <div>
                      <div className="card new-joinee-card">
                        <h4>New Joinee</h4>
                        <div className="profile-info">
                          <img src="/assets/images/prasenjit-mukherjee.jpg" alt="Prasenjit Mukherjee" className="profile-pic" />
                          <h5>Prasenjit Mukherjee</h5>
                          <p>Head IT & Digital</p>
                          <p>prasenjit.mukherjee@jwil.com</p>
                          <p className="joined-date">joined on: 01/06/2025</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card new-joinee-card">
                        <h4>New Joinee</h4>
                        <div className="profile-info">
                          <img src="/assets/images/manoj-kumar-mishra.jpg" alt="Anita Sharma" className="profile-pic" />
                          <h5>Anita Sharma</h5>
                          <p>Senior Software Engineer</p>
                          <p>anita.sharma@jwil.com</p>
                          <p className="joined-date">joined on: 28/05/2025</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card new-joinee-card">
                        <h4>New Joinee</h4>
                        <div className="profile-info">
                          <img src="/assets/images/prasenjit-mukherjee.jpg" alt="Rohit Patel" className="profile-pic" />
                          <h5>Rohit Patel</h5>
                          <p>Business Analyst</p>
                          <p>rohit.patel@jwil.com</p>
                          <p className="joined-date">joined on: 25/05/2025</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card new-joinee-card">
                        <h4>New Joinee</h4>
                        <div className="profile-info">
                          <img src="/assets/images/manoj-kumar-mishra.jpg" alt="Kavya Singh" className="profile-pic" />
                          <h5>Kavya Singh</h5>
                          <p>UI/UX Designer</p>
                          <p>kavya.singh@jwil.com</p>
                          <p className="joined-date">joined on: 20/05/2025</p>
                        </div>
                      </div>
                    </div>
                  </Slider>
                </div>

                {/* Birthdays Carousel */}
                <div className="birthdays-carousel">
                  <Slider
                    key={`birthday-slider-${sliderKey}`}
                    dots={true}
                    infinite={true}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    autoplay={true}
                    autoplaySpeed={5000}
                    arrows={false}
                    adaptiveHeight={false}
                    lazyLoad="ondemand"
                    customPaging={(i) => (
                      <div className="custom-dot">
                        <span></span>
                      </div>
                    )}
                    dotsClass="slick-dots custom-dots"
                  >
                    <div>
                      <div className="card birthdays-card">
                        <h4>Birthdays & Anniversaries</h4>
                        <div className="profile-info">
                          <img src="/assets/images/manoj-kumar-mishra.jpg" alt="Manoj Kumar Mishra" className="profile-pic" />
                          <h5>Manoj Kumar Mishra</h5>
                          <p>Birthday Today • Engineering Team</p>
                          <button className="wish-btn"><span className="icon-send"></span> Wish</button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card birthdays-card">
                        <h4>Birthdays & Anniversaries</h4>
                        <div className="profile-info">
                          <img src="/assets/images/prasenjit-mukherjee.jpg" alt="Priya Sharma" className="profile-pic" />
                          <h5>Priya Sharma</h5>
                          <p>Work Anniversary • HR Team</p>
                          <button className="wish-btn"><span className="icon-send"></span> Wish</button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card birthdays-card">
                        <h4>Birthdays & Anniversaries</h4>
                        <div className="profile-info">
                          <img src="/assets/images/manoj-kumar-mishra.jpg" alt="Rajesh Kumar" className="profile-pic" />
                          <h5>Rajesh Kumar</h5>
                          <p>Birthday Tomorrow • Operations Team</p>
                          <button className="wish-btn"><span className="icon-send"></span> Wish</button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="card birthdays-card">
                        <h4>Birthdays & Anniversaries</h4>
                        <div className="profile-info">
                          <img src="/assets/images/prasenjit-mukherjee.jpg" alt="Anjali Patel" className="profile-pic" />
                          <h5>Anjali Patel</h5>
                          <p>5 Year Anniversary • Finance Team</p>
                          <button className="wish-btn"><span className="icon-send"></span> Wish</button>
                        </div>
                      </div>
                    </div>
                  </Slider>
                </div>

                {/* Leave Card */}
                <div className="card leave-card">
                  <h4>Leave & Attendance</h4>
                  <div className="calendar">
                    <div className="calendar-header">
                      <button className="nav-arrow prev" onClick={() => navigateMonth('prev')}>&lt;</button>
                      <span className="month-year">{getMonthName(currentDate)}</span>
                      <button className="nav-arrow next" onClick={() => navigateMonth('next')}>&gt;</button>
                    </div>
                    <div className="calendar-grid">
                      {renderCalendarDays()}
                    </div>
                  </div>
                  <div className="leave-balance">
                    <p>Leave Balance <br /><span>Annual Leaves</span></p>
                    <p className="balance-count">19/<span>30</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Image Carousel Modal */}
        {showCarouselModal && (
          <div className="carousel-modal-overlay" onClick={() => setShowCarouselModal(false)}>
            <div className="carousel-modal-container" onClick={(e) => e.stopPropagation()}>
              <button 
                className="carousel-modal-close" 
                onClick={() => setShowCarouselModal(false)}
              >
                &times;
              </button>
              <ImageCarousel />
            </div>
          </div>
        )}

        {/* Core Values */}
        <section className="core-values-section">
          <div className="container">
            <div className="core-values-content">
              <div className="section-header">
                <h2>OUR CORE VALUES</h2>
                <p>Let's <span>LIVE</span> our Core Values and <span>REAP</span> the success</p>
              </div>
              <div className="core-values-grid">
                <div className="value-card reliability">
                  <div className="value-icon-wrapper">
                    <img src="/assets/images/preseverance.png" alt="RELIABILITY" />
                  </div>
                  <div className="value-content">
                    <p>We are trustworthy and reliable in thought and action</p>
                    <div className="value-title">RELIABILITY</div>
                  </div>
                </div>
                <div className="value-card excellence">
                  <div className="value-icon-wrapper">
                    <img src="/assets/images/preseverance.png" alt="EXCELLENCE" />
                  </div>
                  <div className="value-content">
                    <p>We strive for goals with passion to achieve the set benchmarks</p>
                    <div className="value-title">EXCELLENCE</div>
                  </div>
                </div>
                <div className="value-card agility">
                  <div className="value-icon-wrapper">
                    <img src="/assets/images/preseverance.png" alt="AGILITY" />
                  </div>
                  <div className="value-content">
                    <p>We are agile un approach to navigate through changes and deliver results</p>
                    <div className="value-title">AGILITY</div>
                  </div>
                </div>
                <div className="value-card perseverance">
                  <div className="value-icon-wrapper">
                    <img src="/assets/images/preseverance.png" alt="PERSEVERANCE" />
                  </div>
                  <div className="value-content">
                    <p>We ensure the resilience and commitment to overcome challenge</p>
                    <div className="value-title">PERSEVERANCE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* JWIL Updates */}
        <section className="jwil-updates-section">
          <div className="container">
            <div className="updates-layout">
              {/* Main Updates Content */}
              <div className="updates-main">
                <nav className="updates-tabs">
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'JWIL Latest Updates' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('JWIL Latest Updates'); }}
                  >
                    JWIL Latest Updates
                  </a>
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'Know Your Company' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('Know Your Company'); }}
                  >
                    Know Your Company
                  </a>
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'Company Policies' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('Company Policies'); }}
                  >
                    Company Policies
                  </a>
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'Snippets' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('Snippets'); }}
                  >
                    Snippets
                  </a>
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'Events' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('Events'); }}
                  >
                    Events
                  </a>
                  <a 
                    href="#" 
                    className={`tab-link ${activeTab === 'Reward and Recognitions' ? 'active' : ''}`}
                    onClick={(e) => { e.preventDefault(); setActiveTab('Reward and Recognitions'); }}
                  >
                    Reward and Recognitions
                  </a>
                  <a href="#" className="tab-link icon-edit"></a>
                </nav>
                
                {/* Conditional Content Based on Active Tab */}
                {activeTab === 'JWIL Latest Updates' && (
                  <div className="updates-container">
                    <div className="updates-list">
                      {(showAllUpdates ? jwilUpdates : jwilUpdates.slice(0, 6)).map((update, index) => (
                        <div key={index} className="update-item">
                          <img src="/assets/images/update-img.png" alt="Update Image" className="update-img" />
                          <div className="update-content">
                            <h5>{update.title}</h5>
                            <p>{update.description}</p>
                            <span className="update-date">{update.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!showAllUpdates && jwilUpdates.length > 6 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllUpdates(true);
                          }}
                        >
                          View All
                        </button>
                      </div>
                    )}
                    {showAllUpdates && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllUpdates(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Reward and Recognitions Tab Content */}
                {activeTab === 'Reward and Recognitions' && (
                  <div className="awards-container">
                    <div className="awards-layout">
                      {/* Central REAP Logo */}
                      <div className="reap-center">
                        <div className="reap-letter reliability">R<span>RELIABILITY</span></div>
                        <div className="reap-letter excellence">E<span>EXCELLENCE</span></div>
                        <div className="reap-letter agility">A<span>AGILITY</span></div>
                        <div className="reap-letter perseverance">P<span>PERSEVERANCE</span></div>
                      </div>
                      
                      {/* Dashed connecting lines */}
                      <svg className="connection-lines" viewBox="0 0 800 400">
                        {awardsData.map((employee, index) => {
                          const startX = parseFloat(employee.position.left) * 8;
                          const startY = parseFloat(employee.position.top) * 4;
                          const endX = 400;
                          const endY = 200;
                          return (
                            <line
                              key={`line-${employee.id}`}
                              x1={startX}
                              y1={startY}
                              x2={endX}
                              y2={endY}
                              stroke="#00a0e3"
                              strokeWidth="2"
                              strokeDasharray="5,5"
                              opacity="0.6"
                            />
                          );
                        })}
                      </svg>
                      
                      {/* Employee Awards */}
                      {awardsData.map((employee) => (
                        <div key={employee.id}>
                          <div 
                            className="award-employee"
                            style={{ 
                              position: 'absolute',
                              top: employee.position.top,
                              left: employee.position.left
                            }}
                          >
                            <div className="employee-image-container">
                              <img 
                                src={employee.image} 
                                alt={employee.name}
                                className="employee-award-image"
                              />
                              <div className="employee-name-tooltip">{employee.name}</div>
                            </div>
                          </div>
                          <div 
                            className="award-badge-positioned"
                            style={{
                              position: 'absolute',
                              top: employee.badgePosition.top,
                              left: employee.badgePosition.left
                            }}
                          >
                            {employee.award}
                          </div>
                        </div>
                      ))}
                      
                      {/* Empty Award Placeholders matching reference design */}
                      <div className="empty-award" style={{ position: 'absolute', top: '5%', left: '8%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '5%', left: '25%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '25%', left: '5%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '75%', left: '5%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '75%', left: '85%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '25%', left: '95%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '5%', left: '95%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '45%', left: '95%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                      <div className="empty-award" style={{ position: 'absolute', top: '75%', left: '45%' }}>
                        <div className="empty-placeholder"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Company Policies Tab Content */}
                {activeTab === 'Company Policies' && (
                  <div className="policies-container">
                    <div className="policies-grid">
                      {(showAllPolicies ? companyPolicies : companyPolicies.slice(0, 9)).map((policy) => (
                        <div 
                          key={policy.id} 
                          className="policy-card"
                          onClick={() => handlePolicyClick(policy)}
                        >
                          <div className="policy-header">
                            <div className="policy-icon">
                              <img 
                                src={policy.type === 'document' ? '/assets/images/doc-icon.svg' : '/assets/images/link-icon.svg'} 
                                alt={policy.type === 'document' ? 'Document' : 'Link'} 
                                className="policy-icon-img" 
                              />
                            </div>
                            <h5>{policy.title}</h5>
                          </div>
                          <div className="policy-details">
                            <div className="policy-meta">
                              <span className="policy-category">{policy.category}</span>
                              <span className="policy-type">{policy.type === 'document' ? 'Document' : 'Link'}</span>
                            </div>
                            <span className="policy-date">{policy.uploadDate}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!showAllPolicies && companyPolicies.length > 9 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllPolicies(true);
                          }}
                        >
                          View All
                        </button>
                      </div>
                    )}
                    {showAllPolicies && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllPolicies(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Snippets Tab Content */}
                {activeTab === 'Snippets' && (
                  <div className="snippets-container">
                    <div className="snippets-list">
                      {(showAllSnippets ? snippets : snippets.slice(0, 6)).map((snippet) => (
                        <div 
                          key={snippet.id} 
                          className="snippet-item"
                          onClick={() => handleSnippetClick(snippet)}
                          style={{ cursor: 'pointer' }}
                        >
                          <img 
                            src={snippet.image || "/assets/images/update-img.png"} 
                            alt="Snippet Image" 
                            className="snippet-img"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/assets/images/update-img.png";
                            }}
                          />
                          <div className="snippet-content">
                            <h5>{snippet.title}</h5>
                            <p>{snippet.description}</p>
                            <div className="snippet-meta">
                              <span className={`snippet-type ${snippet.type}`}>{snippet.type.charAt(0).toUpperCase() + snippet.type.slice(1)}</span>
                              <span className="snippet-date">{snippet.date}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!showAllSnippets && snippets.length > 6 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllSnippets(true);
                          }}
                        >
                          View All
                        </button>
                      </div>
                    )}
                    {showAllSnippets && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllSnippets(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Events Tab Content */}
                {activeTab === 'Events' && (
                  <div className="events-container">
                    <div className="events-grid">
                      {(showAllEvents ? events : events.slice(0, 6)).map((event) => (
                        <div 
                          key={event.id} 
                          className="event-card"
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="event-image">
                            <img 
                              src={event.image || "/assets/images/banner-image.jpg"} 
                              alt={event.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "/assets/images/banner-image.jpg";
                              }}
                            />
                            <div className="event-date-badge">
                              <span className="event-month">{event.eventDate.split(' ')[0]}</span>
                              <span className="event-day">{event.eventDate.split(' ')[1].replace(',', '')}</span>
                            </div>
                          </div>
                          <div className="event-content">
                            <h3 className="event-title">{event.title}</h3>
                            <p className="event-description">{event.description}</p>
                            <div className="event-details">
                              <div className="event-time">
                                <span className="icon-clock"></span>
                                <span>{event.eventTime}</span>
                              </div>
                              {event.entryFee && (
                                <div className="event-fee">
                                  <span className="icon-ticket"></span>
                                  <span>{event.entryFee}</span>
                                </div>
                              )}
                            </div>
                            <div className="event-actions">
                              <button className="event-btn primary">Learn More</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!showAllEvents && events.length > 6 && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllEvents(true);
                          }}
                        >
                          View All Events
                        </button>
                      </div>
                    )}
                    {showAllEvents && (
                      <div className="view-all-container">
                        <button 
                          className="view-all-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllEvents(false);
                          }}
                        >
                          Show Less
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Other Tab Contents - Placeholder */}
                {activeTab !== 'JWIL Latest Updates' && activeTab !== 'Reward and Recognitions' && activeTab !== 'Company Policies' && activeTab !== 'Snippets' && activeTab !== 'Events' && (
                  <div className="tab-content-placeholder">
                    <h3>{activeTab}</h3>
                    <p>Content for {activeTab} will be displayed here.</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="updates-sidebar">
                <div className="sidebar-card company-metrics-card">
                  <div className="card-header">
                    <h6>Company Metrics</h6>
                    <span className="icon-edit"></span>
                  </div>
                  <div className="metrics-grid">
                    <div className="metric-item">
                      <p>Total Expenses</p>
                      <div className="metric-value">42</div>
                      <div className="metric-change down"><span className="icon-down-arrow"></span> 12% Last Week</div>
                    </div>
                    <div className="metric-item">
                      <p>Revenue Generated</p>
                      <div className="metric-value">28.2 M</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 8% Last Month</div>
                    </div>
                    <div className="metric-item">
                      <p>Response Time</p>
                      <div className="metric-value">15.8 hrs</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 5% Last Week</div>
                    </div>
                    <div className="metric-item">
                      <p>Response Time</p>
                      <div className="metric-value">15.8 hrs</div>
                      <div className="metric-change up"><span className="icon-up-arrow"></span> 5% Last Week</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

      {/* Image Modal */}
      {showImageModal && selectedMedia && (
        <div className="media-modal-overlay" onClick={closeImageModal}>
          <div className="media-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="media-modal-close" onClick={closeImageModal}>
              &times;
            </button>
            <img 
              src={selectedMedia.link} 
              alt={selectedMedia.title} 
              className="modal-image"
            />
            <div className="modal-caption">
              <h3>{selectedMedia.title}</h3>
              <p>{selectedMedia.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && selectedMedia && (
        <div className="media-modal-overlay" onClick={closeVideoModal}>
          <div className="media-modal-container video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="media-modal-close" onClick={closeVideoModal}>
              &times;
            </button>
            <iframe
              src={selectedMedia.link}
              title={selectedMedia.title}
              className="modal-video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <div className="modal-caption">
              <h3>{selectedMedia.title}</h3>
              <p>{selectedMedia.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/assets/images/jwil-logo.svg" alt="JWIL Logo" className="footer-logo-img" />
            </div>
            <div className="footer-links">
              <a href="#" className="footer-link">Privacy Policy</a>
              <a href="#" className="footer-link">Terms of Service</a>
              <a href="#" className="footer-link">Contact Us</a>
            </div>
            <div className="social-icons">
              <a href="#" className="social-icon" aria-label="Facebook">
                <i className="icon-facebook"></i>
              </a>
            </div>
            <div className="copyright">
              &copy; Copyright 2024 All Rights Reserved by JWIL
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Boat Icon */}
      <div className="floating-chat-boat">
        <button className="chat-boat-btn" aria-label="Chat Support" title="Chat with us">
          <img src="/assets/images/chat-boat.svg" alt="Chat Boat Icon" className="chat-boat-icon" />
        </button>
      </div>
      </main>
    </>
  );
};

export default HomePage;
